import { useId, useState, type ReactNode } from 'react';
import { EmptyState } from './EmptyState.js';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import { Tabs } from './Tabs.js';
import './HelpPanel.css';

export interface HelpTopic {
  title: string;
  body: ReactNode;
  /** The prototype's "Plain-language glossary" block under the topic. */
  glossary?: { term: string; definition: ReactNode }[];
}

export interface HelpPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current screen's help content, looked up by the caller (e.g. by
   * route) and handed in — this component does not know about routing.
   * `undefined` renders the documented empty state rather than a blank
   * panel, for a screen that hasn't had its help copy written yet. */
  topic: HelpTopic | undefined;
  /**
   * The "Ask Dedupr" tab's content — an `Assistant`.
   *
   * **Every product app is expected to pass this** (owner decision 2026-07-29:
   * the `?` icon shows two tabs, Documentation and Ask Dedupr, and all frontends
   * behave identically). Omitting it still degrades to a single-heading panel, so
   * a surface genuinely without an assistant is not broken — but a *product* app
   * omitting it is the inconsistency that decision exists to remove.
   * `apps/admin` did exactly that and shipped a visibly different help panel from
   * the two customer apps.
   */
  assistant?: ReactNode;
}

/**
 * The screen-aware contextual help panel (F29 ui-spec, `/goal`-mandated:
 * "sidebar with inline help in ui"). **Where it is opened from changed on
 * 2026-07-28** (owner decision): the topbar's "Help and guidance" circle, not a
 * pinned row in the nav — the prototype has exactly one help affordance and the
 * build briefly had two. The requirement's substance is unchanged, and is now
 * enforced by `AppShell` refusing to be constructed without `onHelpClick`.
 *
 * **Docked, not an overlay drawer.** The prototype renders this as an inline
 * `<aside>` with a left hairline, no scrim and no shadow, which *shrinks the main
 * column* rather than floating over it — so the help and the screen it describes
 * are readable at the same time. That is the entire point of contextual help, and
 * rendering it as a Radix dialog (scrim, focus trap, content hidden behind it)
 * defeated it (audit S-8). No focus trap for the same reason: this is a companion
 * pane, not a modal.
 *
 * The tab pair ("Documentation" / "Ask Dedupr") and the glossary block are the
 * prototype's own structure, both previously missing. The first tab was labelled
 * "On this page" until 2026-07-29; it is "Documentation" by owner decision, which
 * also reads better beside "Ask Dedupr" — one names a source, the other an action.
 */
export function HelpPanel({ open, onOpenChange, topic, assistant }: HelpPanelProps) {
  const [tab, setTab] = useState('docs');
  /* Ids are generated here rather than inside `Tabs` because the panel has to
     point back at the selected tab with `aria-labelledby`, and it cannot guess a
     value React generated in a child. Without this pair the `tablist` role
     announced "tab, 1 of 2" and led nowhere — the role promises a `tabpanel` and
     nothing linked them. */
  const baseId = useId();
  const tabsId = `${baseId}-tabs`;
  const panelId = `${baseId}-panel`;
  if (!open) return null;

  const showTabs = Boolean(assistant);

  return (
    <aside className="dpr-help-panel" aria-label="Help and guidance">
      <div className="dpr-help-panel__head">
        {showTabs ? (
          <Tabs
            label="Help view"
            value={tab}
            onChange={setTab}
            idPrefix={tabsId}
            controls={panelId}
            items={[
              { id: 'docs', label: 'Documentation' },
              { id: 'ask', label: 'Ask Dedupr' },
            ]}
          />
        ) : (
          <h2 className="dpr-help-panel__title">Documentation</h2>
        )}
        <IconButton
          size="sm"
          variant="ghost"
          icon={<Icon name="close" size={16} />}
          label="Close help panel"
          onClick={() => onOpenChange(false)}
        />
      </div>

      <div
        className="dpr-help-panel__body"
        id={panelId}
        role={showTabs ? 'tabpanel' : undefined}
        aria-labelledby={showTabs ? `${tabsId}-${tab}` : undefined}
      >
        {showTabs && tab === 'ask' ? (
          assistant
        ) : topic ? (
          <>
            <div className="dpr-help-panel__topic">
              <h3 className="dpr-help-panel__topic-title">{topic.title}</h3>
              <div className="dpr-help-panel__topic-body">{topic.body}</div>
            </div>
            {topic.glossary && topic.glossary.length > 0 ? (
              <div className="dpr-help-panel__glossary">
                <h4 className="dpr-help-panel__glossary-title">Plain-language glossary</h4>
                <dl className="dpr-help-panel__glossary-list">
                  {topic.glossary.map((entry) => (
                    <div key={entry.term} className="dpr-help-panel__glossary-entry">
                      <dt>{entry.term}</dt>
                      <dd>{entry.definition}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
          </>
        ) : (
          <EmptyState
            title="No help written for this screen yet"
            description="This screen doesn't have contextual guidance yet."
          />
        )}
      </div>
    </aside>
  );
}
